import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function Stars({ n }) {
  return (
    <span style={{ color: '#ffd970', letterSpacing: 1 }}>
      {Array.from({ length: 5 }, (_, i) => (i < n ? '★' : '☆')).join('')}
    </span>
  );
}

export default function AdminRatings() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filterBook, setFilterBook] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/ratings', { headers: authHeaders() }),
      axios.get('/api/admin/books',   { headers: authHeaders() }),
    ])
      .then(([r, b]) => {
        if (!alive) return;
        setItems(Array.isArray(r.data) ? r.data : []);
        setBooks(Array.isArray(b.data) ? b.data : []);
      })
      .catch((e) => alive && setErr(e?.response?.data?.message || 'Failed to load'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const bookMap = useMemo(() => {
    const m = new Map();
    books.forEach((b) => m.set(String(b._id), b));
    return m;
  }, [books]);

  const filtered = useMemo(() => {
    if (!filterBook) return items;
    return items.filter((r) => String(r.bookId) === filterBook);
  }, [filterBook, items]);

  const summary = useMemo(() => {
    const groups = new Map();
    items.forEach((r) => {
      const id = String(r.bookId);
      const g = groups.get(id) || { sum: 0, n: 0 };
      g.sum += r.value; g.n += 1;
      groups.set(id, g);
    });
    return Array.from(groups.entries()).map(([id, g]) => ({
      bookId: id,
      title: bookMap.get(id)?.title || 'Unknown book',
      count: g.n,
      avg: g.n ? +(g.sum / g.n).toFixed(2) : 0,
    })).sort((a, b) => b.count - a.count);
  }, [items, bookMap]);

  const onDelete = async (r) => {
    if (!window.confirm('Delete this rating?')) return;
    try {
      await axios.delete(`/api/admin/ratings/${r._id}`, { headers: authHeaders() });
      setItems((arr) => arr.filter((x) => x._id !== r._id));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1 className="admin-page-title">Ratings</h1>
        <p className="admin-page-sub">{items.length} total ratings across {summary.length} books.</p>
      </header>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}

      <div className="admin-card">
        <h2>Per-book averages</h2>
        <div className="admin-table-wrap" style={{ marginTop: 6 }}>
          <table className="admin-table">
            <thead>
              <tr><th>Book</th><th>Ratings</th><th>Average</th><th></th></tr>
            </thead>
            <tbody>
              {summary.length === 0 && !loading ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, opacity: 0.7 }}>No ratings yet.</td></tr>
              ) : summary.map((s) => (
                <tr key={s.bookId}>
                  <td><strong style={{ color: 'var(--admin-fg-strong)' }}>{s.title}</strong></td>
                  <td>{s.count}</td>
                  <td><Stars n={Math.round(s.avg)} /> <span style={{ marginLeft: 8, opacity: 0.75 }}>{s.avg}</span></td>
                  <td><button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setFilterBook(s.bookId)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-toolbar" style={{ marginTop: 16 }}>
        <select className="admin-select admin-search" value={filterBook} onChange={(e) => setFilterBook(e.target.value)}>
          <option value="">All books</option>
          {summary.map((s) => <option key={s.bookId} value={s.bookId}>{s.title} ({s.count})</option>)}
        </select>
        {filterBook && <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setFilterBook('')}>Clear filter</button>}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Book</th>
              <th>Rating</th>
              <th>User</th>
              <th>When</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No ratings.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r._id}>
                <td><strong style={{ color: 'var(--admin-fg-strong)' }}>{bookMap.get(String(r.bookId))?.title || r.bookId}</strong></td>
                <td><Stars n={r.value} /></td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', opacity: 0.75 }}>{String(r.userId).slice(-6)}</td>
                <td style={{ fontSize: '0.82rem', opacity: 0.75 }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                <td>
                  <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(r)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
